/**
 * ModelConfigRenderer - Image generation model configuration
 *
 * Used by Generate, Edit, and Fuse nodes for model selection and parameters.
 * D3 migration: uses catalog-native ModelSelect + ImageConfigPanel instead of
 * the legacy playground ModelSelector + /v1/images/models-driven dropdowns.
 * Note: This renderer does not support field mapping as model config is not mappable.
 */

import React from 'react';
import { ModelSelect } from '@/components/model-display/ModelSelect';
import { ImageConfigPanel } from '@/components/model-display/ImageConfigPanel';
import { useCatalogModels } from '@/lib/queries/useCatalogModels';
import type { BaseCustomRendererProps } from './types';

export const ModelConfigRenderer: React.FC<BaseCustomRendererProps> = ({
  nodeData,
  isDisabled = false,
  nodeType,
  onBatchUpdate,
}) => {
  const operationMode =
    nodeType === 'edit' ? 'edit' : nodeType === 'fuse' ? 'fuse' : 'generate';

  const selectedModelId =
    nodeData.model && nodeData.model !== 'auto' ? (nodeData.model as string) : '';

  const { data: catalogModels = [] } = useCatalogModels('image');
  const selectedCatalogModel = catalogModels.find((m) => m.id === selectedModelId);

  const handleModelChange = (modelId: string) => {
    onBatchUpdate?.({ model: modelId });
  };

  const handleConfigChange = (patch: {
    size?: string;
    aspect_ratio?: string;
    quality?: string;
  }) => {
    // Normalize empty-string values to undefined so they don't leak into NodeData as ''
    const normalized: { size?: string; aspect_ratio?: string; quality?: string } = {};
    if ('size' in patch) normalized.size = patch.size || undefined;
    if ('aspect_ratio' in patch) normalized.aspect_ratio = patch.aspect_ratio || undefined;
    if ('quality' in patch) normalized.quality = patch.quality || undefined;
    onBatchUpdate?.(normalized);
  };

  return (
    <div className="space-y-4">
      <ModelSelect
        value={selectedModelId}
        onChange={handleModelChange}
        modality="image"
        operationFilter={operationMode}
        disabled={isDisabled}
        variant="compact"
      />

      {selectedCatalogModel && (
        <ImageConfigPanel
          model={selectedCatalogModel}
          value={{
            size: nodeData.size,
            aspect_ratio: nodeData.aspect_ratio,
            quality: nodeData.quality,
          }}
          onChange={handleConfigChange}
          disabled={isDisabled}
        />
      )}
    </div>
  );
};

export default ModelConfigRenderer;
